/** Overview, Processes, Runs, and the sign-in screen. */

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ApiError, Dashboard, ProcessSummary, RunSummary, api, session } from "../lib/api";
import { bytes, duration, number, relative, runDuration } from "../lib/format";
import { useAsync, useNow, usePolling } from "../lib/useLive";
import { Icon } from "../components/icons";
import {
  BrandMark, ConfirmDeleteProcess, Empty, ErrorBanner, Loading, PageHeader,
  SearchField, Segmented, StateBadge, Stat,
} from "../components/ui";

// ------------------------------------------------------------------ login

export function Login({ onSignedIn }: { onSignedIn: () => void }) {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [workspaceName, setWorkspaceName] = useState("My Workspace");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = mode === "login"
        ? await api.login(email, password)
        : await api.register({ email, password, workspace_name: workspaceName });
      session.set(result.access_token, result.workspaces[0]?.id ?? "");
      onSignedIn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "could not reach the API");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-screen">
      <div className="auth-visual">
        <div className="auth-stage" aria-hidden>
          <div className="auth-sweep" />
          <div className="auth-ring" />
          <div className="auth-ring" />
          <div className="auth-ring" />
        </div>
        <div className="auth-copy">
          <div className="row" style={{ gap: 12 }}>
            <BrandMark size={16} />
            <div>
              <div className="brand-name">HOLOXI</div>
              <div className="hint">Instrumentation for live video</div>
            </div>
          </div>
          <h1>Persistent AI on live HoloPod video.</h1>
          <p>
            Build, deploy and monitor video AI workflows from one workspace,
            with continuous server-side execution across sessions and devices.
          </p>
        </div>
        <div className="auth-points">
          <div className="auth-point">
            <Icon name="holopods" size={16} />
            <div>
              <strong>Watch the stream, not the machine</strong>
              <span>Pods, apps and storage. Workers stay out of the way.</span>
            </div>
          </div>
          <div className="auth-point">
            <Icon name="apps" size={16} />
            <div>
              <strong>Turn live video into useful data</strong>
              <span>Run OCR, detection, tracking and more on every stream.</span>
            </div>
          </div>
          <div className="auth-point">
            <Icon name="data" size={16} />
            <div>
              <strong>Every run leaves a record</strong>
              <span>Artifacts, versions and events stay with the process that made them.</span>
            </div>
          </div>
        </div>
      </div>

      <div className="auth-form-col">
        <div className="auth-card stack">
          <div className="row" style={{ gap: 12 }}>
            <BrandMark size={16} />
            <div>
              <div className="brand-name">HOLOXI</div>
              <div className="hint">Sign in to your workspace</div>
            </div>
          </div>

          <div className="auth-tabs" role="tablist">
            <button
              type="button"
              className={mode === "login" ? "active" : ""}
              onClick={() => { setMode("login"); setError(null); }}
            >
              Sign in
            </button>
            <button
              type="button"
              className={mode === "register" ? "active" : ""}
              onClick={() => { setMode("register"); setError(null); }}
            >
              Create workspace
            </button>
          </div>

          <form className="card stack" onSubmit={submit}>
            <h2>{mode === "login" ? "Welcome back" : "Set up HoloXI"}</h2>
            {error && <ErrorBanner error={error} />}
            <label className="field">
              Email
              <input type="email" value={email} required autoComplete="username"
                     onChange={(event) => setEmail(event.target.value)} />
            </label>
            <label className="field">
              Password
              <input type="password" value={password} required minLength={8}
                     autoComplete={mode === "login" ? "current-password" : "new-password"}
                     onChange={(event) => setPassword(event.target.value)} />
            </label>
            {mode === "register" && (
              <label className="field">
                Workspace name
                <input type="text" value={workspaceName}
                       onChange={(event) => setWorkspaceName(event.target.value)} />
              </label>
            )}
            <button className="btn primary" type="submit" disabled={busy}>
              {busy ? "Working…" : mode === "login" ? "Sign in" : "Create workspace"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// --------------------------------------------------------------- overview

export function Overview() {
  const { data, error } = usePolling<Dashboard>(() => api.dashboard(), 4000);
  const now = useNow(Boolean(data?.running.length));

  if (error && !data) return <div className="page"><ErrorBanner error={error} /></div>;
  if (!data) return <div className="page"><Loading rows={4} /></div>;

  const storagePercent = data.storage.quota_bytes
    ? Math.min(100, (data.storage.bytes_used / data.storage.quota_bytes) * 100)
    : 0;

  return (
    <div className="page stack">
      <PageHeader
        title="Overview"
        description="Everything below runs on the server. Closing this page does not stop any of it."
        actions={<Link className="btn primary" to="/processes/new"><Icon name="plus" size={14} />New process</Link>}
      />

      <div className="grid cols-4">
        <Stat
          label="HoloPods"
          icon="holopods"
          value={data.sources.online}
          sub={`${data.sources.offline} offline · ${data.sources.total} registered`}
          tone={data.sources.online > 0 ? "signal" : undefined}
        />
        <Stat
          label="Running processes"
          icon="runs"
          value={data.runs.running}
          sub={`${data.runs.degraded} degraded · ${data.runs.completed} completed`}
          tone={data.runs.degraded > 0 ? "amber" : data.runs.running > 0 ? "signal" : undefined}
        />
        <Stat
          label="Active workers"
          icon="compute"
          value={data.compute.workers_online}
          sub={`${data.compute.gpu_workers} GPU workers · ${data.compute.active_runs} active runs`}
        />
        <Stat
          label="Storage"
          icon="storage"
          value={bytes(data.storage.bytes_used)}
          sub={
            <>
              of {bytes(data.storage.quota_bytes)}
              <div className="meter" style={{ marginTop: 8 }}>
                <div style={{ width: `${storagePercent}%` }} />
              </div>
            </>
          }
        />
      </div>

      <div className="grid split">
        <section className="stack">
          <div className="section-head">
            <h2>Running processes</h2>
            <div className="spacer" />
            <Link className="linkish" to="/processes">View all</Link>
          </div>
          {data.running.length === 0 ? (
            <Empty
              icon="runs"
              title="Nothing is running"
              body="Start a process and it will keep running on the server until you stop it — you can close this tab."
              action={<Link className="btn primary" to="/processes/new"><Icon name="plus" size={14} />Create a process</Link>}
            />
          ) : (
            <div className="grid cols-2">
              {data.running.map((run) => (
                <Link key={run.run_id} to={`/runs/${run.run_id}`} className="card interactive stack">
                  <div className="row">
                    <StateBadge state={run.state} />
                    <div className="spacer" />
                    <span className="id">{run.holopod_id ?? run.source_id}</span>
                  </div>
                  <div>
                    <div className="card-title">{run.process_name}</div>
                    <div className="hint">{run.apps.join(" · ")}</div>
                  </div>
                  <div className="metric-row">
                    <div className="metric-cell">
                      <div className="stat-label">Runtime</div>
                      <div className="mono" style={{ fontSize: 14 }}>
                        {duration(runDuration({
                          started_at: run.started_at,
                          duration_seconds: run.duration_seconds,
                        }, now))}
                      </div>
                    </div>
                    <div className="metric-cell">
                      <div className="stat-label">FPS</div>
                      <div className="mono" style={{ fontSize: 14, color: "var(--signal)" }}>
                        {run.decoded_fps ? run.decoded_fps.toFixed(1) : "—"}
                      </div>
                    </div>
                    <div className="metric-cell">
                      <div className="stat-label">Rows</div>
                      <div className="mono" style={{ fontSize: 14 }}>{number(run.rows_written)}</div>
                    </div>
                    <div className="metric-cell">
                      <div className="stat-label">Data</div>
                      <div className="mono" style={{ fontSize: 14 }}>{bytes(run.bytes_written)}</div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        <section className="stack">
          <div className="section-head">
            <h2>Recent events</h2>
            <div className="spacer" />
            <Link className="linkish" to="/events">Event log</Link>
          </div>
          <div className="card">
            {data.recent_events.length === 0 ? (
              <p className="hint" style={{ margin: 0 }}>No events yet.</p>
            ) : (
              <div className="log">
                {data.recent_events.map((event) => (
                  <div className="log-line" key={event.id}>
                    <span className="log-time">{new Date(event.timestamp).toLocaleTimeString([], { hour12: false })}</span>
                    <span className={`log-type ${event.severity}`}>{event.event_type}</span>
                    <span style={{ color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {event.message}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

// -------------------------------------------------------------- processes

export function Processes() {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("");
  const [removing, setRemoving] = useState<ProcessSummary | null>(null);
  const { data, error, loading, reload } = useAsync<ProcessSummary[]>(() => api.processes(), []);
  const navigate = useNavigate();

  const rows = (data ?? []).filter((process) => {
    if (search && !process.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (filter === "running") return Boolean(process.active_run);
    if (filter === "idle") return !process.active_run;
    return true;
  });

  return (
    <div className="page stack">
      <PageHeader
        title="Processes"
        description="A process is a saved pipeline: source, apps, outputs. Run it as many times as you like."
        actions={<Link className="btn primary" to="/processes/new"><Icon name="plus" size={14} />New process</Link>}
      />

      <div className="toolbar">
        <SearchField value={search} onChange={setSearch} placeholder="Search by name" />
        <Segmented
          value={filter}
          onChange={setFilter}
          options={[
            { value: "", label: "All" },
            { value: "running", label: "Running" },
            { value: "idle", label: "Idle" },
          ]}
        />
      </div>

      {error && <ErrorBanner error={error} onRetry={reload} />}
      {loading && <Loading rows={4} />}

      {!loading && rows.length === 0 && (
        <Empty
          icon="processes"
          title={data?.length ? "No process matches that filter" : "No processes yet"}
          body={
            data?.length
              ? "Try a different search or clear the filter."
              : "Build one from a HoloPod, an AI app and an output. It takes about a minute."
          }
          action={<Link className="btn primary" to="/processes/new"><Icon name="plus" size={14} />New process</Link>}
        />
      )}

      {rows.length > 0 && (
        <div className="card flush">
          <table>
            <thead>
              <tr>
                <th>Process</th><th>Source</th><th>Apps</th><th>State</th>
                <th>Last run</th><th>Version</th><th />
              </tr>
            </thead>
            <tbody>
              {rows.map((process) => (
                <tr key={process.id} className="clickable" onClick={() => navigate(`/processes/${process.id}`)}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{process.name}</div>
                    {process.description && <div className="hint">{process.description}</div>}
                  </td>
                  <td style={{ color: "var(--text-muted)" }}>{process.source_name ?? "—"}</td>
                  <td>
                    <div className="row wrap" style={{ gap: 4 }}>
                      {process.app_ids.map((appId, index) => (
                        <span className="tag" key={`${appId}-${index}`}>{appId}</span>
                      ))}
                    </div>
                  </td>
                  <td>
                    {process.active_run
                      ? <StateBadge state={process.active_run.state} />
                      : <span className="hint">idle</span>}
                  </td>
                  <td style={{ color: "var(--text-muted)" }}>
                    {process.last_run ? relative(process.last_run.started_at) : "never"}
                  </td>
                  <td className="mono">v{process.current_version}</td>
                  <td style={{ textAlign: "right" }}>
                    <div className="row" style={{ justifyContent: "flex-end", gap: 6 }}
                         onClick={(event) => event.stopPropagation()}>
                      {process.active_run ? (
                        <Link className="btn sm" to={`/runs/${process.active_run.id}`}>
                          Open live
                        </Link>
                      ) : (
                        <button
                          className="btn sm primary"
                          onClick={async () => {
                            const run = await api.start(process.id);
                            navigate(`/runs/${run.id}`);
                          }}
                        >
                          <Icon name="play" size={12} />
                          Run
                        </button>
                      )}
                      <button
                        className="btn sm ghost"
                        style={{ color: "var(--coral)" }}
                        disabled={Boolean(process.active_run)}
                        title={process.active_run ? "Stop the run before deleting this process" : "Delete process"}
                        onClick={() => setRemoving(process)}
                      >
                        <Icon name="trash" size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {removing && (
        <ConfirmDeleteProcess
          name={removing.name}
          versionCount={removing.current_version}
          onClose={() => setRemoving(null)}
          onConfirm={async (purgeData) => {
            await api.deleteProcess(removing.id, purgeData);
            reload();
          }}
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------------ runs

export function Runs() {
  const [state, setState] = useState("");
  const { data, error } = usePolling<RunSummary[]>(
    () => api.runs(state ? `?state=${state}` : ""), 4000, true, [state]);
  const hasLive = (data ?? []).some((run) => !run.stopped_at && run.started_at);
  const now = useNow(hasLive);
  const navigate = useNavigate();
  const loading = !data && !error;

  return (
    <div className="page stack">
      <PageHeader
        title="Runs"
        description="Every start creates a run with a frozen copy of the configuration it used."
      />

      <div className="toolbar">
        <Segmented
          value={state}
          onChange={setState}
          options={[
            { value: "", label: "All" },
            { value: "RUNNING", label: "Running" },
            { value: "DEGRADED", label: "Degraded" },
            { value: "COMPLETED", label: "Completed" },
            { value: "FAILED", label: "Failed" },
            { value: "CANCELLED", label: "Cancelled" },
          ]}
        />
      </div>

      {error && <ErrorBanner error={error} />}
      {loading && <Loading rows={5} />}

      {!loading && (data?.length ?? 0) === 0 && (
        <Empty icon="runs" title="No runs yet" body="Start a process and its run will appear here with its artifacts." />
      )}

      {(data?.length ?? 0) > 0 && (
        <div className="card flush">
          <table>
            <thead>
              <tr><th>Run</th><th>State</th><th>Started</th><th>Duration</th><th>Worker</th><th>Restarts</th></tr>
            </thead>
            <tbody>
              {data!.map((run) => (
                <tr key={run.id} className="clickable" onClick={() => navigate(`/runs/${run.id}`)}>
                  <td className="mono">{run.id}</td>
                  <td><StateBadge state={run.state} reason={run.state_reason} /></td>
                  <td style={{ color: "var(--text-muted)" }}>{relative(run.started_at)}</td>
                  <td className="mono">{duration(runDuration(run, now))}</td>
                  <td className="id">{run.worker_id ?? "—"}</td>
                  <td className="mono" style={{ color: run.restart_count ? "var(--amber)" : undefined }}>
                    {run.restart_count}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
